/**
 * @fileoverview a wrapper for highcharts so we can bridge the existing analytics
 *   experiences with React
 */

import React, { useEffect, useMemo, useRef } from "react";

import classnames from "classnames";
import HighchartsReact from "highcharts-react-official";

import { Icon } from "@braze/beacon-core";
import { useId } from "@braze/beacon-hooks";

import Highcharts from "@lib/highcharts";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import useI18nNamespace from "@services/useI18nNamespace";

import ErrorBoundary from "@components/ErrorBoundary";

import { HighchartsWrapper as StyledContainer } from "./HighchartsWrapper.style";
import { chartDataParameters, chartDataToolDescription } from "./constants";
import type { HighchartsWrapperProps, OperatorChartDataSliceOptions, OperatorTimeSeriesDataSlice } from "./types";
import { getOperatorChartDataAccess, getOperatorTimeSeriesDataSlice } from "./utils";

export const HighchartsWrapper = ({
  className,
  chartConfig,
  allowChartUpdate,
  immutable,
  ErrorBoundaryButtonComponent,
}: HighchartsWrapperProps): React.ReactElement => {
  const { registerTool } = useAgenticAiContext();
  const { t } = useI18nNamespace("highcharts-wrapper");
  const operatorId = useId("highcharts-chart");
  // Walks and copies the whole series graph, so keep it off the render path: it
  // only changes when the chart config does.
  const operatorDataAccess = useMemo(
    () => getOperatorChartDataAccess(chartConfig, operatorId),
    [chartConfig, operatorId]
  );
  const operatorDataRef = useRef(operatorDataAccess?.data);
  operatorDataRef.current = operatorDataAccess?.data;
  const requiresChartDataTool = operatorDataAccess?.requiresTool === true;

  useEffect(() => {
    if (!requiresChartDataTool) {
      return;
    }

    const cleanup = registerTool({
      toolEntityId: operatorId,
      name: "get_chart_data",
      description: chartDataToolDescription,
      parameters: chartDataParameters,
      callback: (args: OperatorChartDataSliceOptions): OperatorTimeSeriesDataSlice => {
        const data = operatorDataRef.current;
        if (!data) {
          throw new Error("Chart data is no longer available.");
        }

        return getOperatorTimeSeriesDataSlice(data, args);
      },
      options: {
        toolGroup: "Analysis",
        labels: {
          ready: t("read-chart-data-ready", "Preparing to read chart data"),
          running: t("read-chart-data-running", "Reading chart data"),
          complete: t("read-chart-data-complete", "Read chart data"),
          failed: t("read-chart-data-failed", "Failed to read chart data"),
          stopped: t("read-chart-data-stopped", "Stopped reading chart data"),
        },
        requiresApproval: "no",
        allowJsonPathTraversal: true,
        toolIcon: <Icon name={["far", "chart-line"]} />,
        argumentsSummaryText: () =>
          operatorDataRef.current?.series.map(({ name }) => name).join(", ") || t("chart-data", "Chart data"),
      },
    });

    return cleanup;
  }, [registerTool, operatorId, requiresChartDataTool, t]);

  return (
    <ErrorBoundary
      team="frontend_ix"
      featureId="HighChartsWrapper-HighchartsReact"
      ButtonComponent={ErrorBoundaryButtonComponent}
    >
      <StyledContainer
        id={operatorId}
        className={classnames("db-highcharts-wrapper", className)}
        data-operator-description={operatorDataAccess?.description}
      >
        <HighchartsReact
          allowChartUpdate={allowChartUpdate}
          highcharts={Highcharts}
          options={chartConfig}
          immutable={immutable}
        />
      </StyledContainer>
    </ErrorBoundary>
  );
};
